import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  Quote, 
  Star, 
  User, 
  Volume2, 
  VolumeX, 
  PlayCircle 
} from 'lucide-react';
import styles from './Feedback.module.css';

const feedbackData = [
  {
    id: 1,
    type: 'video',
    videoUrl: 'https://res.cloudinary.com/ddkyeuhk8/video/upload/v1774964873/anantainfotech/kuxujbnntsnu0kayr9mi.webm', 
    name: 'Rajeev Kumar',
    role: 'MD, CityNews India',
    quote: "The AI anchors have revolutionized our daily broadcasting. We now produce 24/7 content with zero downtime."
  },
  {
    id: 2,
    type: 'text',
    text: "Ananta AI Anchor completely transformed how our local news channel operates. We produce 5x more content daily without the massive overhead of a physical studio. The lip-sync and expressions are game-changers.",
    name: 'Priya Patel',
    role: 'Editor in Chief',
    rating: 5
  },
  {
    id: 3,
    type: 'video',
    videoUrl: 'https://res.cloudinary.com/ddkyeuhk8/video/upload/v1774963976/anantainfotech/vteepobtjvwe08w9log6.mp4',
    name: 'Amit Verma',
    role: 'Senior Producer, Digital Pulse',
    quote: "I was skeptical about AI, but the results are mind-blowing. Our viewers genuinely believe these are human anchors."
  },
  {
    id: 4,
    type: 'text',
    text: "Creating my own custom AI clone saved me 40 hours a week. I just upload the script and my digital twin does the rest. It's the most profitable investment I've made for my channel this year.",
    name: 'Sneha Rao',
    role: 'Journalist',
    rating: 5
  }
];

const Feedback = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);

  // --- UNMUTE ON FIRST GLOBAL CLICK ---
  useEffect(() => {
    const handleFirstInteraction = () => {
      setIsMuted(false);
      setHasInteracted(true);
      window.removeEventListener('click', handleFirstInteraction);
    };
    window.addEventListener('click', handleFirstInteraction);
    return () => window.removeEventListener('click', handleFirstInteraction);
  }, []);

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % feedbackData.length);
  };

  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + feedbackData.length) % feedbackData.length);
  };

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 500 : -500,
      opacity: 0,
      scale: 0.95
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 500 : -500,
      opacity: 0,
      scale: 0.95
    })
  };

  const current = feedbackData[currentIndex];

  return (
    <section className={styles.feedbackSection} id="feedback">
      <div className={styles.glowOrb} />
      
      <div className={styles.wrapper}>
        {/* HEADER */}
        <div className={styles.header}>
          <div className={styles.badge}>CLIENT SUCCESS</div>
          <h2 className={styles.title}>
            Trusted by <span className={styles.gradientText}>Digital Newsrooms</span>
          </h2>
          <p className={styles.subtitle}>Watch and read how Ananta AI is scaling digital journalism.</p>
        </div>

        {/* CAROUSEL CONTAINER */}
        <div className={styles.carouselContainer}>
          <button className={styles.navBtn} onClick={handlePrev}>
            <ChevronLeft size={24} />
          </button>

          <div className={styles.cardViewport}>
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                className={styles.feedbackCard}
              >
                {/* LEFT SIDE: VISUALS (Fixed 320px width) */}
                <div className={styles.visualSide}>
                  {current.type === 'video' ? (
                    <div className={styles.portraitFrame}>
                      <video 
                        key={current.videoUrl}
                        src={current.videoUrl} 
                        autoPlay 
                        muted={isMuted} 
                        loop 
                        playsInline
                        className={styles.videoPlayer}
                      />
                      <div className={styles.videoOverlay}>
                        <div className={styles.tag}>9:16 PREVIEW</div>
                        <button 
                          className={styles.volumeToggle} 
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsMuted(!isMuted);
                          }}
                        >
                          {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} color="#e87355" />}
                        </button>
                      </div>
                      {!hasInteracted && (
                        <div className={styles.unmuteHint}>Click anywhere to hear voice</div>
                      )}
                    </div>
                  ) : (
                    <div className={styles.placeholderSide}>
                      <Quote className={styles.quoteIconGhost} size={80} />
                      <div className={styles.brandBadge}>ANANTA AI</div>
                    </div>
                  )}
                </div>

                {/* RIGHT SIDE: CONTENT */}
                <div className={styles.contentSide}>
                  <div className={styles.stars}>
                    {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="#e87355" color="#e87355" />)}
                  </div>
                  
                  <div className={styles.textContainer}>
                    <p className={styles.mainQuote}>
                      {current.type === 'video' ? `"${current.quote}"` : `"${current.text}"`}
                    </p>
                  </div>

                  <div className={styles.authorMeta}>
                    <div className={styles.avatar}>
                      {current.type === 'text' ? current.name.charAt(0) : <User size={22} />}
                    </div>
                    <div className={styles.authorInfo}>
                      <h4 className={styles.name}>{current.name}</h4>
                      <p className={styles.role}>{current.role}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <button className={styles.navBtn} onClick={handleNext}>
            <ChevronRight size={24} />
          </button>
        </div>

        {/* INDICATORS */}
        <div className={styles.indicatorWrapper}>
          {feedbackData.map((_, idx) => (
            <div 
              key={idx} 
              className={`${styles.dot} ${idx === currentIndex ? styles.activeDot : ''}`}
              onClick={() => {
                setDirection(idx > currentIndex ? 1 : -1);
                setCurrentIndex(idx);
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Feedback;